import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface CarouselState {
  showCarousel: boolean;
  setShowCarousel: (value: boolean) => void;
  toggleCarousel: () => void;
  showMeetingView: boolean;
  setShowMeetingView: (value: boolean) => void;
  zoomFactor: number;
  setZoomFactor: (value: number) => void;
  // Dynamic ceiling on zoomFactor, published by whichever FloorMap instance is
  // currently visible (based on its own container/image dimensions) so zoom-in
  // never grows the image past its container's width. Defaults to MAX_ZOOM
  // until a FloorMap has measured itself.
  maxZoomFactor: number;
  setMaxZoomFactor: (value: number) => void;
}

export const DEFAULT_ZOOM = 1;
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 3;

export const useCarouselStore = create<CarouselState>()(
  persist(
    (set) => ({
      showCarousel: true,
      showMeetingView: false,
      zoomFactor: DEFAULT_ZOOM,
      maxZoomFactor: MAX_ZOOM,

      setShowCarousel: (value: boolean) => set({ showCarousel: value }),

      toggleCarousel: () =>
        set((state) => ({
          showCarousel: !state.showCarousel,
        })),

      setShowMeetingView: (value: boolean) => set({ showMeetingView: value }),

      setZoomFactor: (value: number) => set({ zoomFactor: value }),

      setMaxZoomFactor: (value: number) => set({ maxZoomFactor: value }),
    }),
    {
      name: 'carousel-store',
      partialize: (state) => ({
        zoomFactor: state.zoomFactor,
      }),
    }
  )
);

// The user's floor map visibility from just before they entered an auditorium; null while
// they are not in one. Kept outside the store and the views so it survives the office view
// unmounting mid-auditorium and is reachable from the meeting leave path.
let floorMapShownBeforeAuditorium: boolean | null = null;

/** Hides the floor map on entering an auditorium, remembering what it was. Repeat calls in
 *  the same stay (e.g. a role-change re-join) are no-ops, so a user's own toggle sticks. */
export const hideFloorMapForAuditorium = () => {
  if (floorMapShownBeforeAuditorium !== null) return;
  floorMapShownBeforeAuditorium = useCarouselStore.getState().showCarousel;
  useCarouselStore.getState().setShowCarousel(false);
};

/** Puts the floor map back the way it was before the auditorium; a no-op outside one. */
export const restoreFloorMapAfterAuditorium = () => {
  if (floorMapShownBeforeAuditorium === null) return;
  useCarouselStore.getState().setShowCarousel(floorMapShownBeforeAuditorium);
  floorMapShownBeforeAuditorium = null;
};
